/**
 * ServicePinCard — reveal the per-device service-mode PIN.
 *
 * The PIN is what an operator types into customer-mobile's LoginScreen
 * (10× tap on the legalese footer → PIN dialog) to launch the DPC's
 * EnrollmentActivity for FRP verification, manual snapshot, or
 * re-enrollment without physical adb access.
 *
 * Behaviour
 *   - The card is invisible to users without the `device.service_pin.view`
 *     permission. Support / recovery roles get it by default; broader
 *     read-only scopes do not.
 *   - The PIN is NOT fetched on page load — only when the operator
 *     clicks "Reveal." Every reveal goes through the server-side audit
 *     log (action='device.service-pin.revealed', payload includes the
 *     calling user). This keeps the audit feed clean: one row per
 *     deliberate operator action, not per page-view.
 *   - Once revealed, the PIN auto-hides after 60s (or on manual hide /
 *     unmount). The 60s window matches the typical "open admin-web →
 *     walk to customer → tap 10× → type PIN" cycle.
 *   - 404 from the endpoint means the device has no active snapshot
 *     key (pre-enrollment or post-offboarding). Surfaced as an
 *     explanatory line, not an error toast — the cheat itself wouldn't
 *     work on that device either.
 *
 * No queryKey — the mutation is fire-and-forget. We deliberately don't
 * cache because (a) auditability + (b) the value is constant for the
 * lifetime of the snapshot key, so caching across a session would
 * encourage operators to "look it up earlier" — defeats the audit.
 */
import { useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Badge } from "./Badge.js";
import { Button } from "./Button.js";
import { Card, CardBody, CardHeader, CardTitle } from "./Card.js";
import { api } from "../lib/api.js";

interface ServicePinResponse {
  readonly pin: string;
  readonly derivedAt: string;
}

const AUTO_HIDE_MS = 60_000;

export function ServicePinCard({ deviceId }: { readonly deviceId: string }): JSX.Element {
  const [pin, setPin] = useState<string | null>(null);
  const [derivedAt, setDerivedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reveal = useMutation({
    mutationFn: () => api.get<ServicePinResponse>(`/api/devices/${deviceId}/service-pin`),
    onSuccess: (res) => {
      setPin(res.pin);
      setDerivedAt(res.derivedAt);
      setError(null);
    },
    onError: (err: unknown) => {
      setPin(null);
      setDerivedAt(null);
      // ApiError carries `status` + RFC-7807 detail; collapse to a
      // human-readable line. 404 has its own message — the device
      // can't host the cheat in its current state.
      const e = err as { status?: number; problem?: { detail?: string } };
      if (e.status === 404) {
        setError(
          "No active snapshot key for this device — service PIN cannot be derived. " +
            "Device is either pre-enrollment or post-offboarding.",
        );
      } else {
        setError(e.problem?.detail ?? "Failed to reveal service PIN.");
      }
    },
  });

  // Auto-hide PIN after 60s so a screenshare / shoulder-surf window is
  // bounded. Operators who need it longer can re-reveal (and re-audit).
  useEffect(() => {
    if (pin === null) return;
    const timeout = window.setTimeout(() => {
      setPin(null);
      setDerivedAt(null);
    }, AUTO_HIDE_MS);
    return () => window.clearTimeout(timeout);
  }, [pin]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Service PIN</CardTitle>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-slate-600">
          The 6-digit code an operator types into customer-mobile&apos;s LoginScreen (10×-tap the
          &quot;Masharti na Sera ya Faragha&quot; footer → PIN dialog) to launch the DPC&apos;s
          EnrollmentActivity. Use for FRP verification, manual snapshot, or re-enrollment without
          adb access. Each reveal is audited.
        </p>

        {pin === null ? (
          <div className="flex items-center gap-3">
            <Button onClick={() => reveal.mutate()} disabled={reveal.isPending} variant="primary">
              {reveal.isPending ? "Revealing…" : "Reveal PIN"}
            </Button>
            {error !== null ? <span className="text-xs text-red-700">{error}</span> : null}
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold tracking-widest text-slate-900">
                {pin}
              </span>
              <Badge tone="warning">Auto-hides in 60s</Badge>
              <Button
                onClick={() => {
                  setPin(null);
                  setDerivedAt(null);
                }}
                variant="ghost"
              >
                Hide
              </Button>
            </div>
            {derivedAt !== null ? (
              <p className="text-xs text-slate-500">
                Derived at {new Date(derivedAt).toLocaleTimeString()}. PIN stays constant until the
                device re-enrolls (snapshot key rotation).
              </p>
            ) : null}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
